import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Features,
    Locations,
    ProductTypes,
    RoutePaths
} from '../../Constants/Common';
import ModalDialog from '../common/ModalDialog';
import ProgressButton from '../common/ProgressButton';
import { VehicleType } from '../common/CommonTypes';
import Policies from './Policies';
import { useTranslation } from 'react-i18next';
import { SiteContext } from '../../context/SiteContext';

export type PolicySectionProps = {
    vehicles: VehicleType[];
};

const PolicySection = ({ vehicles }: PolicySectionProps): JSX.Element => {
    if (!vehicles || vehicles.length === 0) {
        return <></>;
    }

    const { t, i18n } = useTranslation();

    const [showDialog, setShowDialog] = useState(false);

    const [selectedPolicyReference, setSelectedPolicyReference] = useState('');

    const [showModalForClaim, setShowModalForClaim] = useState(false);

    const navigate = useNavigate();

    const { features } = useContext(SiteContext);

    const getSelectedPolicy = () => {
        if (selectedPolicyReference === '') {
            return null;
        }

        return vehicles
            .map((v) => v.policies)
            .reduce((a, b) => a.concat(b), [])
            .find((p) => p.reference === selectedPolicyReference);
    };

    const isSiteLocationUs = i18n.language?.toLowerCase() === Locations.Us;

    const onRequestRepairClicked = (event: React.FormEvent) => {
        if (selectedPolicyReference === '') {
            event.preventDefault();
        } else {
            const policy = getSelectedPolicy();
            if (
                policy &&
                (policy.type === ProductTypes.JointAlloyCosmetic ||
                    policy.type === ProductTypes.JointAlloyTyre)
            ) {
                event.preventDefault();
                setShowDialog(true);
            } else {
                if (
                    isSiteLocationUs &&
                    policy &&
                    policy.type !== ProductTypes.GAP
                ) {
                    event.preventDefault();
                    const state = {
                        product: policy,
                        productRoute: getProductRoute(policy)
                    };

                    navigate(RoutePaths.RepairRequestPreCheckPage, { state });
                } else navigate(getProductRoute(policy));
            }
        }
    };

    const onPriorRepairRequestClicked = (event: React.FormEvent) => {
        if (selectedPolicyReference === '') {
            event.preventDefault();
        }
    };

    const onViewDocumentsClicked = (event: React.FormEvent) => {
        if (selectedPolicyReference === '') {
            event.preventDefault();
        }
        const state = {
            product: getSelectedPolicy()
        };
        navigate(RoutePaths.ViewDocuments, { state });
    };

    const onOpenNewClaimClicked = (event: React.FormEvent) => {
        event.preventDefault();

        if (
            features.GapClaim === Features.GapClaim.Off ||
            i18n.language.toLowerCase() !== Locations.Us
        ) {
            setShowModalForClaim(true);
            setShowDialog(true);
        } else {
            const state = {
                product: getSelectedPolicy()
            };
            navigate(RoutePaths.ClaimDetails, { state });
        }
    };

    const onCloseModalClick = () => {
        setShowDialog(false);
        setShowModalForClaim(false);
    };

    const onJointAlloyConfirmation = () => {
        handleJointNavigation(RoutePaths.Alloy);
    };

    const onJointCosmeticConfirmation = () => {
        handleJointNavigation(RoutePaths.Cosmetic);
    };

    const onJointTyreConfirmation = () => {
        handleJointNavigation(RoutePaths.Tyre);
    };

    const handleJointNavigation = (routePath: string) => {
        const state = {
            product: getSelectedPolicy(),
            productRoute: routePath
        };
        if (isSiteLocationUs || routePath === RoutePaths.Tyre) {
            navigate(RoutePaths.RepairRequestPreCheckPage, { state });
        } else {
            navigate(routePath, { state });
        }
    };

    const sharedrequestButtonStyle = 'btn btn-primary progress-button';
    const policy = getSelectedPolicy();
    const requestButtonStyle =
        policy && policy.status === 'Active'
            ? `${sharedrequestButtonStyle}`
            : `${sharedrequestButtonStyle} disabled`;

    const requestButtonDocumentStyle = policy
        ? `${sharedrequestButtonStyle}`
        : `${sharedrequestButtonStyle} disabled`;

    const getProductRoute = (selectedProduct) => {
        switch (selectedProduct?.type) {
            case ProductTypes.Alloy:
            case ProductTypes.AlloyRepair:
                return RoutePaths.Alloy;
            case ProductTypes.Cosmetic:
            case ProductTypes.CosmeticRepair:
                return RoutePaths.Cosmetic;
            case ProductTypes.JointAlloyCosmetic:
            case ProductTypes.JointAlloyTyre:
                return RoutePaths.ToResolveFromProductType;
            case ProductTypes.Tyre:
                return RoutePaths.Tyre;
            default:
                return undefined;
        }
    };
    const selectedProduct = getSelectedPolicy();

    const isGermanUserWithoutGapProduct =
        (i18n.language.toLowerCase() === Locations.De ||
            i18n.language.toLowerCase() === Locations.At) &&
        selectedProduct &&
        selectedProduct.type !== ProductTypes.GAP
            ? true
            : false;

    // UKTyreClaim feature flag
    const isUKTyreClaimFeatureOff =
        features.UKTyreClaim === Features.UKTyreClaim.Off &&
        i18n.language?.toLowerCase() !== Locations.Us;
    const isTyreClaimsUnavailable =
        isUKTyreClaimFeatureOff && policy?.type === ProductTypes.Tyre;
    const isTyreButtonDisabled =
        isUKTyreClaimFeatureOff && policy?.type === ProductTypes.JointAlloyTyre;

    const createProductNavigation = () => {
        if (isTyreClaimsUnavailable) {
            return (
                <p className='error'>{t('option_not_available_for_product')}</p>
            );
        }

        if (isGermanUserWithoutGapProduct) {
            return <></>;
        } else {
            if (
                selectedProduct &&
                getProductRoute(selectedProduct) === undefined
            ) {
                if (selectedProduct.type === ProductTypes.GAP) {
                    return (
                        <ProgressButton
                            onClick={onOpenNewClaimClicked}
                            navRoute=''
                            buttonStyle={requestButtonStyle}
                            text={t('open_a_claim')}
                        />
                    );
                } else {
                    return (
                        <p className='error'>
                            {selectedProduct.type === ProductTypes.Warranty
                                ? t('option_not_available_for_product')
                                : t('cannot_request_a_repair_for_this_product')}
                        </p>
                    );
                }
            }
        }

        const productRoute = getProductRoute(selectedProduct) || '/';

        return (
            <ProgressButton
                onClick={onRequestRepairClicked}
                navRoute={productRoute}
                locationState={{ product: selectedProduct }}
                buttonStyle={requestButtonStyle}
                text={t('request_a_repair')}
            />
        );
    };
    const createPriorRepairRequestButton = () => {
        if (isGermanUserWithoutGapProduct) {
            return <div></div>;
        }

        return (
            <ProgressButton
                onClick={onPriorRepairRequestClicked}
                navRoute={RoutePaths.ViewRequests}
                locationState={{
                    product: getSelectedPolicy()
                }}
                buttonStyle={requestButtonStyle}
                text={t('prior_repair_requests')}
            />
        );
    };

    const navButton = createProductNavigation();
    const renderPriorRepairRequestButton = createPriorRepairRequestButton();

    const renderPolicies = (vehicle: VehicleType, i: number) => {
        return (
            <div id={`vehicle-${i.toString()}`}>
                <div className='card-header'>
                    <p className='card-title'>{vehicle?.vrn}</p>
                    <p className='card-title'>{vehicle?.vin}</p>
                    <span className='text-muted'>{`${vehicle?.make} ${vehicle?.model}`}</span>
                </div>
                {vehicle.policies && (
                    <Policies
                        vehicleIndex={i}
                        selectedPolicyReference={selectedPolicyReference}
                        policies={vehicle.policies}
                        onPolicySelected={setSelectedPolicyReference}
                    />
                )}
            </div>
        );
    };

    const renderJointProductDialog = () => {
        return (
            <>
                <button
                    className='btn btn-primary'
                    onClick={onJointAlloyConfirmation}>
                    {t('alloy_wheel')}
                </button>
                {policy && policy.type === ProductTypes.JointAlloyCosmetic ? (
                    <button
                        className='btn btn-primary'
                        onClick={onJointCosmeticConfirmation}>
                        {t('cosmetic')}
                    </button>
                ) : (
                    <button
                        disabled={isTyreButtonDisabled}
                        className='btn btn-primary'
                        onClick={
                            !isTyreButtonDisabled && onJointTyreConfirmation
                        }>
                        {t('tyre')}
                    </button>
                )}
            </>
        );
    };

    const modalTitle =
        policy && policy.type === ProductTypes.JointAlloyCosmetic
            ? t('modal_title_cosmetic')
            : t('modal_title_tyre');

    return (
        <>
            <div className='card formInput--card'>
                <div className='card-header-container'>
                    <div className='card-header header-left'>
                        {t('vehicle_policies')}
                    </div>
                </div>
                {vehicles.map((vehicle, index) => (
                    <div key={`vehicle-${index.toString()}`}>
                        {renderPolicies(vehicle, index)}
                    </div>
                ))}
                <ModalDialog
                    showDialog={showDialog}
                    hideFooter={true}
                    onCloseClick={onCloseModalClick}>
                    <p>
                        {showModalForClaim
                            ? features.GapClaim === Features.GapClaim.Off
                                ? t('help_text.feature_not_available')
                                : t('help_text.open_claim_message')
                            : modalTitle}
                    </p>
                    <div
                        className={
                            showModalForClaim
                                ? 'open-claim-modal-container'
                                : 'joint-alloy-cosmetic-container'
                        }>
                        {showModalForClaim ? (
                            <button
                                className='btn btn-primary'
                                onClick={onCloseModalClick}>
                                {t('button_label.close')}
                            </button>
                        ) : (
                            <>{renderJointProductDialog()}</>
                        )}
                    </div>
                </ModalDialog>
            </div>
            <div className='bottom-navigation'>
                {navButton}
                {renderPriorRepairRequestButton}
                <ProgressButton
                    onClick={onViewDocumentsClicked}
                    navRoute={RoutePaths.ViewDocuments}
                    locationState={{
                        product: getSelectedPolicy()
                    }}
                    buttonStyle={requestButtonDocumentStyle}
                    text={t('view_documents')}
                />
            </div>
        </>
    );
};

export default PolicySection;
